import { useState, useRef, useEffect } from 'react';
import { searchApi } from '../services/api';
import { formatDuration, calculateElapsedMinutes, formatApiError } from '../utils/helpers';
import { X, MagnifyingGlass, CarSimple } from '@phosphor-icons/react';

export default function SearchModal({ onClose, onSelect }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (query.trim().length >= 2) {
      performSearch();
    } else {
      setResults([]);
    }
  }, [query]);

  const performSearch = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await searchApi.search(query.trim());
      setResults(response.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" data-testid="search-modal">
      <div className="bg-[#141414] rounded-xl w-full max-w-lg border border-[#27272A]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#27272A]">
          <h2 className="text-xl font-heading text-white">Buscar Vehículo</h2>
          <button onClick={onClose} className="p-2 hover:bg-[#27272A] rounded-lg transition-colors" data-testid="search-close">
            <X className="w-6 h-6 text-[#A1A1AA]" weight="bold" />
          </button>
        </div>

        {/* Search Input */}
        <div className="p-4">
          <div className="relative">
            <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" weight="bold" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value.toUpperCase())}
              className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg pl-12 pr-4 py-4 text-white text-lg font-mono uppercase tracking-wider focus:border-[#16A34A] focus:outline-none transition-colors"
              placeholder="Patente o espacio..."
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Results */}
        <div className="px-4 pb-4 max-h-80 overflow-y-auto">
          {error && (
            <div className="bg-[#450A0A] border border-[#DC2626] text-[#FCA5A5] px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-center text-[#A1A1AA] py-8">Buscando...</div>
          ) : results.length === 0 && query.length >= 2 ? (
            <div className="text-center text-[#A1A1AA] py-8">No se encontraron resultados</div>
          ) : (
            <div className="space-y-2">
              {results.map((space) => (
                <button
                  key={space.id}
                  onClick={() => onSelect(space)}
                  className="w-full flex items-center gap-4 p-4 bg-[#1C1C1E] border border-[#27272A] rounded-lg hover:bg-[#27272A] hover:border-[#16A34A] transition-all"
                  data-testid={`search-result-${space.label}`}
                >
                  <CarSimple className="w-8 h-8 text-[#DC2626]" weight="duotone" />
                  <div className="flex-1 text-left">
                    <div className="font-mono text-xl text-white tracking-[0.1em]">
                      {space.vehicle?.plate_number}
                    </div>
                    <div className="text-sm text-[#A1A1AA]">
                      Espacio {space.label} • {space.vehicle?.vehicle_type}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-[#A1A1AA]">
                      {formatDuration(calculateElapsedMinutes(space.vehicle?.entry_time))}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
